/**
 * EditUserPanel Component
 *
 * Single-panel edit UI for users in Edit mode.
 * Uses searchable dropdown for licenses, dual-list picker for tenant access,
 * and segmented control for role selection.
 *
 * Features:
 * - Permission-based section visibility
 * - Staged changes with unified save
 * - Loading states
 */

import React, { useState, useEffect } from 'react';
import {
  Button,
  Spinner,
  Text,
  Input,
  Switch,
  Label,
  Dropdown,
  Option,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { AsyncButton } from '../../common/AsyncButton';
import { SearchableMultiSelect } from '../../common/SearchableMultiSelect';
import { SegmentedControl } from '../../common/SegmentedControl';
import { DualListPicker } from '../../common/DualListPicker';
import { useChangeTracker } from '../../../hooks/useChangeTracker';
import { UserApiService } from '../../../services/userApi';
import { getAssignableRoles } from '../rolePermissions';
import type { TenantUser, LicenseInfo } from '../../../types/user';
import type { SearchableMultiSelectItem } from '../../common/SearchableMultiSelect';
import type { SegmentedControlOption } from '../../common/SegmentedControl';
import type { DualListItem } from '../../common/DualListPicker';

interface OrganizationTenant {
  tenant_id: string;
  tenant_name: string;
}

interface AvailableOrganization {
  id: number;
  name: string;
}

interface EditUserPanelProps {
  user: TenantUser;
  tenantId: string;
  onSave: (changes: EditUserChanges) => Promise<void>;
  onCancel: () => void;
  canEditLicenses: boolean;
  canEditRole: boolean;
  canEditTenantAccess: boolean;
  canEditOrganization?: boolean;
  organizationTenants?: OrganizationTenant[];
  availableOrganizations?: AvailableOrganization[];
  organizationId?: number;
  currentUserRole?: string;
  /** Callback when changes state changes (for parent tracking) */
  onChangesUpdate?: (hasChanges: boolean) => void;
  /** Whether save operation is in progress */
  isSaving?: boolean;
  /** Whether viewing users from the home tenant (excludes Tenant User role) */
  isHomeTenant?: boolean;
}

export interface EditUserChanges {
  displayName?: string;
  accountEnabled?: boolean;
  licensesToAdd: string[];
  licensesToRemove: string[];
  roleChange: { from: string; to: string } | null;
  tenantAccessToAdd: string[];
  tenantAccessToRemove: string[];
  organizationChange: { from: number | null; to: number } | null;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXL,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingHorizontalL,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingBottom: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalXS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  sectionTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  sectionContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacingVerticalXXXL,
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    paddingTop: tokens.spacingVerticalL,
    marginTop: tokens.spacingVerticalS,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalM,
  },
  pillContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
});

export const EditUserPanel: React.FC<EditUserPanelProps> = ({
  user,
  tenantId,
  onSave,
  onCancel,
  canEditLicenses,
  canEditRole,
  canEditTenantAccess,
  canEditOrganization = false,
  organizationTenants = [],
  availableOrganizations = [],
  currentUserRole,
  onChangesUpdate,
  isSaving = false,
  isHomeTenant = false,
}) => {
  const styles = useStyles();

  // General info state
  const [displayName, setDisplayName] = useState(user.display_name || '');
  const [accountEnabled, setAccountEnabled] = useState(user.account_enabled ?? true);
  const [displayNameChanged, setDisplayNameChanged] = useState(false);
  const [accountEnabledChanged, setAccountEnabledChanged] = useState(false);

  // Organization state
  const initialOrganizationId = user.organization_id ?? null;
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(initialOrganizationId);
  const organizationChanged = selectedOrganizationId !== initialOrganizationId;

  // Licenses state
  const [availableLicenses, setAvailableLicenses] = useState<LicenseInfo[]>([]);
  const [isLoadingLicenses, setIsLoadingLicenses] = useState(canEditLicenses);

  // Change tracker
  const {
    changes,
    hasChanges: hasTrackedChanges,
    currentLicenses,
    currentTenantAccess,
    currentRole,
    trackLicenseToggle,
    trackRoleChange,
    trackTenantAccessToggle,
  } = useChangeTracker({
    initialLicenses: user.assigned_licenses || [],
    initialRole: user.role || 'managed_tenant_user',
    initialTenantAccess: user.accessible_tenant_ids || [],
  });

  // Fetch available licenses
  useEffect(() => {
    if (!canEditLicenses) {
      setIsLoadingLicenses(false);
      return;
    }

    const fetchLicenses = async () => {
      try {
        const licenses = await UserApiService.getAvailableLicenses(tenantId);
        setAvailableLicenses(licenses);
      } catch {
        // License loading error handled silently - UI will show empty state
      } finally {
        setIsLoadingLicenses(false);
      }
    };

    fetchLicenses();
  }, [tenantId, canEditLicenses]);

  // Calculate if there are any changes
  const hasGeneralChanges = displayNameChanged || accountEnabledChanged;
  const hasAnyChanges = hasTrackedChanges || hasGeneralChanges || organizationChanged;

  // Notify parent of changes state
  useEffect(() => {
    onChangesUpdate?.(hasAnyChanges);
  }, [hasAnyChanges, onChangesUpdate]);

  // Build license items for SearchableMultiSelect
  const licenseItems: SearchableMultiSelectItem[] = availableLicenses.map(license => ({
    id: license.sku_id,
    label: license.display_name,
    availability: license.available,
  }));

  // Build tenant access items for DualListPicker
  const tenantAccessItems: DualListItem[] = organizationTenants.map(tenant => ({
    id: tenant.tenant_id,
    label: tenant.tenant_name,
  }));

  // Build role options - pass organization context to determine if managed_tenant_user is shown
  // When organizationId is provided, we're in organization context (OrganizationDetailPage)
  // When not provided, we're in tenant context (regular Users page) where managed_tenant_user is shown
  // When isHomeTenant is true, exclude managed_tenant_user (Tenant User is not valid for home tenant users)
  const isOrganizationContext = organizationTenants.length > 0;
  const roleOptions: SegmentedControlOption[] = getAssignableRoles(currentUserRole, isOrganizationContext)
    .filter(role => !isHomeTenant || role.value !== 'managed_tenant_user')
    .map(role => ({
      value: role.value,
      label: role.label,
    }));

  // Handle license selection change from SearchableMultiSelect
  const handleLicenseSelectionChange = (newSelectedIds: string[]) => {
    // Find items to add (in new selection but not current)
    const toAdd = newSelectedIds.filter(id => !currentLicenses.includes(id));
    // Find items to remove (in current but not new selection)
    const toRemove = currentLicenses.filter(id => !newSelectedIds.includes(id));

    // Track each change
    toAdd.forEach(id => trackLicenseToggle(id, false)); // false = not currently selected, so add
    toRemove.forEach(id => trackLicenseToggle(id, true)); // true = currently selected, so remove
  };

  // Handle tenant access selection change from DualListPicker
  const handleTenantAccessChange = (newSelectedIds: string[]) => {
    // Find items to add (in new selection but not current)
    const toAdd = newSelectedIds.filter(id => !currentTenantAccess.includes(id));
    // Find items to remove (in current but not new selection)
    const toRemove = currentTenantAccess.filter(id => !newSelectedIds.includes(id));

    // Track each change
    toAdd.forEach(id => trackTenantAccessToggle(id, false)); // false = not currently selected, so add
    toRemove.forEach(id => trackTenantAccessToggle(id, true)); // true = currently selected, so remove
  };

  // Handle display name change
  const handleDisplayNameChange = (value: string) => {
    setDisplayName(value);
    setDisplayNameChanged(value !== (user.display_name || ''));
  };

  // Handle account enabled change
  const handleAccountEnabledChange = (checked: boolean) => {
    setAccountEnabled(checked);
    setAccountEnabledChanged(checked !== (user.account_enabled ?? true));
  };

  // Handle organization change
  const handleOrganizationChange = (organizationId: number) => {
    setSelectedOrganizationId(organizationId);
  };

  // Handle save
  const handleSave = async () => {
    const editChanges: EditUserChanges = {
      displayName: displayNameChanged ? displayName : undefined,
      accountEnabled: accountEnabledChanged ? accountEnabled : undefined,
      licensesToAdd: changes.licensesToAdd,
      licensesToRemove: changes.licensesToRemove,
      roleChange: changes.roleChange,
      tenantAccessToAdd: changes.tenantAccessToAdd,
      tenantAccessToRemove: changes.tenantAccessToRemove,
      organizationChange: organizationChanged && selectedOrganizationId !== null
        ? { from: initialOrganizationId, to: selectedOrganizationId }
        : null,
    };

    await onSave(editChanges);
  };

  // Loading state
  if (isLoadingLicenses) {
    return (
      <div className={styles.loadingContainer} role="progressbar" aria-label="Loading">
        <Spinner label="Loading..." />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* General Info Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <Text className={styles.sectionTitle}>General Information</Text>
        </div>
        <div className={styles.sectionContent}>
          <div className={styles.formField}>
            <Label htmlFor="displayName">Display Name</Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(_, data) => handleDisplayNameChange(data.value)}
            />
          </div>

          <div className={styles.formField}>
            <Label htmlFor="upn">User Principal Name</Label>
            <Input
              id="upn"
              value={user.user_principal_name || ''}
              disabled
              readOnly
            />
          </div>

          <div className={styles.formField}>
            <Switch
              id="accountEnabled"
              label="Account Enabled"
              checked={accountEnabled}
              onChange={(_, data) => handleAccountEnabledChange(data.checked)}
            />
          </div>
        </div>
      </div>

      {/* Licenses Section */}
      {canEditLicenses && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Text className={styles.sectionTitle}>Licenses</Text>
          </div>
          <div className={styles.sectionContent}>
            <SearchableMultiSelect
              items={licenseItems}
              selectedIds={currentLicenses}
              onSelectionChange={handleLicenseSelectionChange}
              placeholder="Select licenses..."
              showAvailability={true}
              disableUnavailable={true}
            />
          </div>
        </div>
      )}

      {/* Role Section */}
      {canEditRole && roleOptions.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Text className={styles.sectionTitle}>Role</Text>
          </div>
          <div className={styles.sectionContent}>
            <SegmentedControl
              ariaLabel="Role"
              options={roleOptions}
              selectedValue={currentRole}
              onChange={trackRoleChange}
            />
          </div>
        </div>
      )}

      {/* Tenant Access Section */}
      {canEditTenantAccess && tenantAccessItems.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Text className={styles.sectionTitle}>Tenant Access</Text>
          </div>
          <div className={styles.sectionContent}>
            <DualListPicker
              availableItems={tenantAccessItems}
              selectedIds={currentTenantAccess}
              onSelectionChange={handleTenantAccessChange}
              availableLabel="Available Tenants"
              selectedLabel="Assigned Tenants"
            />
          </div>
        </div>
      )}

      {/* Organization Section */}
      {canEditOrganization && availableOrganizations.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Text className={styles.sectionTitle}>Organization</Text>
          </div>
          <div className={styles.sectionContent}>
            <div className={styles.formField}>
              <Label htmlFor="organization">Organization</Label>
              <Dropdown
                id="organization"
                aria-label="Organization"
                selectedOptions={selectedOrganizationId !== null ? [String(selectedOrganizationId)] : []}
                value={availableOrganizations.find(org => org.id === selectedOrganizationId)?.name ?? ''}
                onOptionSelect={(_, data) => {
                  const orgId = data.optionValue ? Number(data.optionValue) : null;
                  if (orgId !== null) {
                    handleOrganizationChange(orgId);
                  }
                }}
              >
                {availableOrganizations.map((org) => (
                  <Option key={org.id} value={String(org.id)}>
                    {org.name}
                  </Option>
                ))}
              </Dropdown>
            </div>
          </div>
        </div>
      )}

      {/* Footer Actions */}
      <div className={styles.footer}>
        <div className={styles.actions}>
          <Button appearance="secondary" onClick={onCancel} disabled={isSaving} data-testid="edit-user-cancel-button">
            Cancel
          </Button>
          <AsyncButton
            appearance="primary"
            onClick={handleSave}
            disabled={!hasAnyChanges}
            loading={isSaving}
            loadingText="Saving..."
            data-testid="edit-user-save-button"
          >
            Save
          </AsyncButton>
        </div>
      </div>
    </div>
  );
};
